import { noop } from '@vkontakte/vkjs';
import { createContext, useContext } from 'react';
import { PromiseQueue } from '@djs';
import { ToastProps } from '../Toast/Toast';

export interface ToastQueuePushParams extends Omit<ToastProps, 'onClose'> {
  /**
   * Ключ группировки однотипных тостов. Позволяет схлопнуть череду однотипных уведомлений
   * (например, ряд входящих сообщений от одного и того же человека)
   * - Если в очередь будет отправлен тост с ключом, который уже там имеется,
   * то старый тост будет заменен на новый, при этом сохранив позицию в очереди.
   * - Если в очередь будет отправлен тост с ключом, который показывается в данный момент,
   * то текущий тост незамедлительно сменится новым.
   * - Если ключ не передан (сгенерируется автоматически) или в очередь будет отправлен тост с ключом,
   * которого нет в очереди, то новый тост добавится в конец очереди.
   */
  distinctKey?: string;
  /**
   * Количество миллисекунд, через которое тост удалится из очереди, если
   * он так и не был показан. Если значение не указано, нахождение в очереди бессрочно.
   */
  expires?: number;
  /**
   * Переопределение компонента тоста по-умолчанию
   */
  Component?: React.ComponentType<ToastProps>;
}

export interface ToastQueueContext {
  _queue: PromiseQueue | null;
  /**
   * Добавляет тост в очередь. Возвращает distinctKey.
   */
  push: (params: ToastQueuePushParams) => string;
  /**
   * Убирает тост из очереди
   */
  remove: (distinctKey: string) => void;
  /**
   * Позволяет моментально закрыть открытый тост и заморозить обработку следующих,
   * добавление новых тостов в очередь продолжает работать.
   * Полезно, когда нужно запретить показ тостов на критичных экранах.
   */
  setLocked: React.Dispatch<React.SetStateAction<boolean>>;
}

export const toastQueueContext = createContext<ToastQueueContext>({
  _queue: null,
  push: () => '',
  remove: noop,
  setLocked: noop,
});

export function useToastQueue() {
  return useContext(toastQueueContext);
}

export const ToastQueueConsumer = toastQueueContext.Consumer;
